import { AssignmentNameComponent } from '@agent/features/assignment/assignment-name/assignment-name.component';
import { ResourcePlanningNavBarComponent } from '@agent/features/resource-planning/resource-planning-nav-bar/resource-planning-nav-bar.component';
import { AssignmentParticipantsSectionComponent } from '@agent/layout/nav-bar/assignment-participants-section/assignment-participants-section.component';
import { AgentNotificationsDropdownComponent } from '@agent/layout/nav-bar/notifications/notifications-dropdown/notifications-dropdown.component';
import { SearchBarComponent } from '@agent/layout/search-bar/search-bar.component';
import { StateService } from '@infra/state/state.service';
import { NgClass } from '@angular/common';
import { Component, DestroyRef, inject, type OnInit, TemplateRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Event as RouterEvent, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { type AssignmentDTO, type CustomerDTO, RolodexCompanyDTO, UserDTO, type ChangeLogDTO } from '@api/models';
import { ChangeLogService } from '@api/services/changelog.service';
import { environment } from '@environments/environment';
import { NgbModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { KeyboardClickableDirective } from '@common/directives/keyboard-clickable.directive';
import { PermissionDirective } from '@common/directives/permission.directive';
import { CompanySelectorComponent } from '@agent/layout/nav-bar/user-company-selector.component';
import { SharedNavbarService } from '@system/layout/nav-bar/nav-bar.service';
import { UserProfileEditComponent } from '@shared/users/user-profile-edit/user-profile-edit.component';
import { AuthService } from '@infra/auth/auth.service';
import { SecurityService } from '@infra/auth/security.service';
import { Globals } from '@system/globals';
import { UserPreferenceService } from '@common/services/user-preference.service';
import { PrimeTemplate } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { map } from 'rxjs';
import { toInitials } from '@common/functions/to-initials';

@Component({
  selector: 'app-agent-nav-bar',
  templateUrl: './agent-nav-bar.component.html',
  imports: [
    KeyboardClickableDirective,
    NgbDropdownModule,
    SearchBarComponent,
    AgentNotificationsDropdownComponent,
    CompanySelectorComponent,
    TranslatePipe,
    RouterLinkActive,
    RouterLink,
    PermissionDirective,
    NgClass,
    AssignmentParticipantsSectionComponent,
    DialogModule,
    PrimeTemplate,
    UserProfileEditComponent,
    AssignmentNameComponent,
    ResourcePlanningNavBarComponent,
  ],
})
export class AgentNavBarComponent implements OnInit {
  globals = Globals;

  readonly authService = inject(AuthService);
  readonly router = inject(Router);
  readonly nav = inject(SharedNavbarService);
  private readonly securityService = inject(SecurityService);
  private readonly stateService = inject(StateService);
  private readonly changeLogService = inject(ChangeLogService);
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly modalService = inject(NgbModal);
  private readonly destroyRef = inject(DestroyRef);

  selectedAssignment: AssignmentDTO;
  selectedCustomer: CustomerDTO;
  showTabs: boolean;
  navColor: string;
  showSecondary: boolean;
  userId$ = this.securityService.currentUser$.pipe(map(user => user.id));
  userId = toSignal(this.userId$);

  selectedBookingAgent: RolodexCompanyDTO;
  selectedAccount: RolodexCompanyDTO;

  apiVersion: string;
  assignmentHasOrders = false;
  isResourcePlanningRoute = false;
  version: string = environment.version;

  isGlobalAdmin: boolean;
  isAgentAdmin: boolean;

  showSearchPopup = false;

  loggedInUserAvatarUrl: string = null;
  loggedInUserInitials: string;
  loggedInUserFullName: string;
  isLoggedInUserImage = false;

  displayNewVersionInfo = false;
  changeLog: ChangeLogDTO;

  constructor() {
    this.isGlobalAdmin = this.authService.isGlobalAdmin;
    this.isAgentAdmin = this.authService.isAgentAdmin;

    this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationEnd && event.url != null) {
        this.showSecondary = event.url.includes('/assignment/');
        if (!this.showSecondary) {
          this.stateService.setAssignment(null);
          this.stateService.setCustomer(null);
          this.stateService.setBookingAgent(null);
          this.stateService.setAccount(null);
        }
        if (
          (!this.router.url.includes('/create') && this.router.url.includes('/assignment/')) ||
          this.router.url.includes('/volume/create')
        ) {
          this.navColor = 'bg-lavender';
        } else {
          this.navColor = 'bg-teal-alt';
        }

        this.isResourcePlanningRoute = this.router.url.includes('/resource-planning');
      }
    });
  }

  ngOnInit(): void {
    this.selectedCustomer = null;
    this.selectedBookingAgent = null;
    this.selectedAccount = null;

    this.stateService.currentAssignment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(assignment => {
      this.selectedAssignment = assignment;
      if (assignment != null) {
        this.showTabs = true;
        this.assignmentHasOrders = assignment.hasOrders;
      } else {
        this.showTabs = false;
      }
    });

    this.stateService.currentCustomer$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(customer => {
      this.selectedCustomer = customer;
    });
    this.stateService.currentAccount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(account => {
      this.selectedAccount = account;
    });
    this.stateService.currentBookingAgent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(agent => {
      if (agent != null) {
        if (agent.id == this.authService.companyId) {
          if (this.selectedCustomer == null && this.selectedAccount == null) this.selectedBookingAgent = agent;
          else this.selectedBookingAgent = null;
        } else this.selectedBookingAgent = agent;
      } else this.selectedBookingAgent = null;
    });

    this.nav
      .getVersion()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((version: string) => {
        this.apiVersion = version;

        if (!this.userPreferenceService.readAsBoolean(`changelog_seen_${version}`)) {
          this.changeLogService.get(version, true).subscribe((changelog: ChangeLogDTO) => {
            this.displayNewVersionInfo = true;
            this.changeLog = changelog;
          });
        }
      });

    this.stateService.showSearchPopup$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((show: boolean) => {
      this.showSearchPopup = show;
    });

    this.securityService.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((currentUser: UserDTO) => {
      if (currentUser != null) {
        if (currentUser.avatarUrl != null && currentUser.avatarUrl !== '') {
          this.isLoggedInUserImage = true;
          this.loggedInUserAvatarUrl = currentUser.avatarUrl;
        } else {
          this.isLoggedInUserImage = false;
          this.loggedInUserAvatarUrl = '';
        }
        this.loggedInUserInitials = toInitials(currentUser.fullName);
        this.loggedInUserFullName = currentUser.fullName;
      }
    });
  }

  backToDashboard(): void {
    if (this.authService.hasUserRole || this.authService.isAgentAdmin || this.authService.isGlobalAdmin) {
      this.stateService.showSearchPopup(false);
      this.stateService.setCustomer(null);
      this.stateService.setAssignment(null);
    }
  }

  logout(event: Event): void {
    event.preventDefault();

    this.authService.logout();
  }

  hideNewVersionModal(): void {
    this.displayNewVersionInfo = false;

    this.userPreferenceService.storeBoolean(`changelog_seen_${this.changeLog.version}`, true);
  }

  getChangeLog(): void {
    this.changeLogService.get(this.apiVersion, true).subscribe((changelog: ChangeLogDTO) => {
      this.displayNewVersionInfo = true;
      this.changeLog = changelog;
    });
  }

  editUser(content: TemplateRef<Element>): void {
    this.modalService.open(content, { size: 'xl' });
  }
}
